import React, { useState, useEffect } from 'react';
import {
  Box,
  Button,
  Chip,
  CircularProgress,
  IconButton,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
  useTheme,
  Breadcrumbs,
  Link,
  TablePagination,
  TextField,
} from '@mui/material';
import { ArrowBack, Add, FileUpload, Search, Sync } from '@mui/icons-material';
import { useNavigate, useParams } from 'react-router-dom';
import { AsrsType, ServerVm } from 'api/asrs/server.vm';
import { getServer } from 'api/asrs/get-server';
import { RegisterStationDialog } from 'pages/asrs/RegisterStationDialog';
import { registerStation, RegisterStationBody } from 'api/asrs/register-station';
import { useSnackbar } from 'hooks/use-snackbar';
import { getAsrsServerProducts } from 'api/catalog/get-asrs-server-products';
import { GetAsrsServerProductsVm } from 'api/catalog/get-asrs-server-products.vm';
import { AsrsServerProductVm } from 'api/catalog/asrs-server-product.vm';
import { unmarkAsrsServerProduct } from 'api/catalog/unmark-asrs-server-product';
import { markAsrsServerProduct } from 'api/catalog/mark-asrs-server-product';
import { AsrsServerProductCsvDialog } from 'pages/asrs/AsrsServerProductCsvDialog';
import { syncProducts } from 'api/asrs/sync-products';
import { getStations } from 'api/asrs/get-stations';
import { StationVm } from 'api/asrs/station.vm';
import { AsrsProductSelectionDialog } from 'pages/asrs/AsrsProductSelectionDialog';
import { AssignLocationToStationDialog } from './AssignLocationToStationDialog';
import { assignLocationToStation } from 'api/asrs/assign-location-to-station';
import { unassignLocationFromStation } from 'api/asrs/unassign-location-from-station';
import ConfirmActionDialog from 'components/ConfirmActionDialog';

const AsrsServerPage = () => {
  const theme = useTheme();
  const navigate = useNavigate();
  const { serverId } = useParams<{ serverId: string }>();
  const [server, setServer] = useState<ServerVm | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [openStationDialog, setOpenStationDialog] = useState(false);
  const [products, setProducts] = useState<AsrsServerProductVm[]>([]);
  const [nextOffset, setNextOffset] = useState<string | null>(null);
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(50);
  const [totalProducts, setTotalProducts] = useState(0);
  const [productsLoading, setProductsLoading] = useState(false);
  const [openProductDialog, setOpenProductDialog] = useState(false);
  const [openCsvDialog, setOpenCsvDialog] = useState(false);
  const [productSearchTerm, setProductSearchTerm] = useState('');
  const [stations, setStations] = useState<StationVm[]>([]);
  const [stationsLoading, setStationsLoading] = useState(false);
  const [stationPage, setStationPage] = useState(0);
  const [stationRowsPerPage, setStationRowsPerPage] = useState(10);
  const [totalStations, setTotalStations] = useState(0);
  const [stationOffset, setStationOffset] = useState('');
  const [assignDialogOpen, setAssignDialogOpen] = useState(false);
  const [selectedStation, setSelectedStation] = useState<StationVm | null>(null);
  const [assignLoading, setAssignLoading] = useState(false);
  const [unassignDialogOpen, setUnassignDialogOpen] = useState(false);
  const snackbar = useSnackbar();

  const handleBackNavigation = () => {
    navigate(-1);
  };

  const fetchServerDetails = () => {
    if (!serverId) return;

    setLoading(true);
    setError(null);

    getServer(serverId, {
      success: (response: ServerVm) => {
        setServer(response);
        setLoading(false);
      },
      error: (err) => {
        console.error('Failed to fetch server details:', err);
        setError('Falha ao carregar detalhes do servidor');
        setLoading(false);
      },
    });
  };

  const fetchStations = (offset = '') => {
    if (!serverId) return;

    setStationsLoading(true);

    getStations(serverId, offset, {
      success: (response) => {
        setStations(response.objects);
        setTotalStations(response.totalItems);
        setStationOffset(response.nextOffset || '');
        setStationsLoading(false);
      },
      error: (err) => {
        snackbar.show('Falha ao carregar estações', null, 'error');
        setStationsLoading(false);
      },
    });
  };

  const fetchProducts = (offset: string | null = null) => {
    if (!serverId) return;

    setProductsLoading(true);
    getAsrsServerProducts(serverId, productSearchTerm, offset ?? '', {
      success: (response: GetAsrsServerProductsVm) => {
        setProducts(response.objects);
        setTotalProducts(response.totalItems);
        setNextOffset(response.nextOffset);
        setProductsLoading(false);
      },
      error: (err) => {
        console.error('Failed to fetch products:', err);
        snackbar.show('Falha ao carregar produtos', null, 'error');
        setProductsLoading(false);
      },
    });
  };

  const handleSearch = () => {
    setPage(0);
    fetchProducts();
  };

  const handleRegisterStation = (stationId: string, warehouseId: string, externalId?: string) => {
    const body: RegisterStationBody = {
      stationId,
      warehouseId,
      serverId: serverId!!,
    };

    if (externalId) {
      body.externalId = externalId;
    }

    registerStation(body, {
      success: function (data: void): void {
        fetchStations();
        snackbar.show('Estação registada', null, 'success');
      },
      error: function (err) {
        snackbar.show('Falha ao registar estação', null, 'error');
      },
    });
  };
  const handleMarkProduct = (productId: string) => {
    markAsrsServerProduct(serverId!!, productId, {
      success: function (data: void): void {
        fetchProducts();
        snackbar.show('Produto marcado com sucesso', null, 'success');
      },
      error: function (err) {
        snackbar.show('Falha ao marcar produto', null, 'error');
      },
    });
  };

  const handleUnmarkProduct = (productId: string) => {
    unmarkAsrsServerProduct(serverId!!, productId, {
      success: function (data: void): void {
        fetchProducts();
        snackbar.show('Produto removido com sucesso', null, 'success');
      },
      error: function (err) {
        snackbar.show('Falha ao remover produto', null, 'error');
      },
    });
  };

  const handleSyncProducts = () => {
    syncProducts(serverId!!, {
      success: function (data: void): void {
        snackbar.show('Produtos enviados para sincronização', null, 'success');
      },
      error: function (err) {
        snackbar.show('Falha ao enviar produtos para sincronização', null, 'error');
      },
    });
  };

  const handleChangePage = (event: unknown, newPage: number) => {
    setPage(newPage);
    // For cursor pagination, we can only go to next page using nextOffset
    if (newPage > page && nextOffset) {
      fetchProducts(nextOffset);
    } else if (newPage === 0) {
      // Reset to first page
      fetchProducts(null);
    }
  };

  const handleChangeRowsPerPage = (event: React.ChangeEvent<HTMLInputElement>) => {
    const newRowsPerPage = parseInt(event.target.value, 10);
    setRowsPerPage(newRowsPerPage);
    setPage(0);
    // Reset to first page when changing page size
    fetchProducts(null);
  };

  const handleOpenProductDialog = () => {
    setOpenProductDialog(true);
  };

  const handleStationPageChange = (event: unknown, newPage: number) => {
    setStationPage(newPage);
    fetchStations((newPage * stationRowsPerPage).toString());
  };

  const handleStationRowsPerPageChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const newRowsPerPage = parseInt(event.target.value, 10);
    setStationRowsPerPage(newRowsPerPage);
    setStationPage(0);
    fetchStations('0');
  };

  const handleOpenAssignDialog = (station: StationVm) => {
    setSelectedStation(station);
    setAssignDialogOpen(true);
  };

  const handleOpenUnassignDialog = (station: StationVm) => {
    setSelectedStation(station);
    setUnassignDialogOpen(true);
  };

  const handleAssignLocation = async (locationId: string) => {
    if (!selectedStation) return;
    setAssignLoading(true);
    assignLocationToStation(
      selectedStation.id,
      { locationId },
      {
        success: () => {
          setAssignLoading(false);
          setAssignDialogOpen(false);
          snackbar.show('Localização atribuída com sucesso', null, 'success');
          fetchStations();
        },
        error: () => {
          setAssignLoading(false);
          snackbar.show('Falha ao atribuir localização', null, 'error');
        },
      }
    );
  };

  const handleConfirmUnassign = () => {
    if (!selectedStation) return;
    setAssignLoading(true);
    unassignLocationFromStation(selectedStation.id, {
      success: () => {
        setAssignLoading(false);
        snackbar.show('Localização desatribuída com sucesso', null, 'success');
        fetchStations();
      },
      error: () => {
        setAssignLoading(false);
        snackbar.show('Falha ao desatribuir localização', null, 'error');
      },
    });
    setUnassignDialogOpen(false);
  };

  useEffect(() => {
    fetchServerDetails();
    fetchProducts();
    fetchStations();
  }, [serverId]);

  const getTypeChip = (type: AsrsType) => {
    switch (type) {
      case AsrsType.KARDEX:
        return <Chip label="Kardex" color="primary" size="small" />;
      default:
        return <Chip label="Desconhecido" size="small" />;
    }
  };

  return (
    <Box sx={{ p: 3 }}>
      <Box sx={{ display: 'flex', alignItems: 'center', mb: 3 }}>
        <IconButton onClick={handleBackNavigation} sx={{ mr: 2 }}>
          <ArrowBack />
        </IconButton>
        <Breadcrumbs aria-label="breadcrumb">
          <Link underline="hover" color="inherit" onClick={handleBackNavigation} sx={{ cursor: 'pointer' }}>
            Módulos ASRS
          </Link>
          <Typography color="text.primary">Servidor {serverId}</Typography>
        </Breadcrumbs>
      </Box>

      {loading ? (
        <CircularProgress />
      ) : error ? (
        <Typography variant="body2" color="error">
          {error}
        </Typography>
      ) : server ? (
        <>
          <Paper sx={{ mb: 3, p: 2 }}>
            <Typography variant="h6" gutterBottom>
              Detalhes do Servidor
            </Typography>
            <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 4 }}>
              <Box>
                <Typography variant="body2" color="text.secondary">
                  Nome
                </Typography>
                <Typography>{server.name}</Typography>
              </Box>
              <Box>
                <Typography variant="body2" color="text.secondary">
                  Tipo
                </Typography>
                <Typography>{getTypeChip(server.type)}</Typography>
              </Box>
              <Box>
                <Typography variant="body2" color="text.secondary">
                  Host
                </Typography>
                <Typography>{server.host}</Typography>
              </Box>
              <Box>
                <Typography variant="body2" color="text.secondary">
                  Porta
                </Typography>
                <Typography>{server.port}</Typography>
              </Box>
            </Box>
          </Paper>

          <Box sx={{ mb: 3 }}>
            <Box
              sx={{
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center',
                mb: 2,
              }}
            >
              <Typography variant="h6">Estações</Typography>
              <Button variant="contained" startIcon={<Add />} onClick={() => setOpenStationDialog(true)}>
                Adicionar Estação
              </Button>
            </Box>

            <TableContainer component={Paper}>
              <Table>
                <TableHead sx={{ backgroundColor: theme.palette.grey[800] }}>
                  <TableRow>
                    <TableCell>ID do Armazém</TableCell>
                    <TableCell>Nome</TableCell>
                    <TableCell>Localização</TableCell>
                    <TableCell>ID Externo</TableCell>
                    <TableCell align="right">Ações</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {stationsLoading ? (
                    <TableRow>
                      <TableCell colSpan={5} align="center">
                        <CircularProgress />
                      </TableCell>
                    </TableRow>
                  ) : stations.length === 0 ? (
                    <TableRow>
                      <TableCell colSpan={5} align="center">
                        Nenhuma estação configurada para este servidor
                      </TableCell>
                    </TableRow>
                  ) : (
                    stations.map((station, index) => (
                      <TableRow key={`${station.id}-${station.warehouseId}-${index}`} hover>
                        <TableCell>{station.warehouseId}</TableCell>
                        <TableCell>{station.id}</TableCell>
                        <TableCell>
                          {station.locationId ? (
                            <Typography variant="body2" color="text.primary">
                              {station.locationId}
                            </Typography>
                          ) : (
                            <Typography variant="body2" color="text.secondary">
                              Não atribuída
                            </Typography>
                          )}
                        </TableCell>
                        <TableCell>
                          {station.externalId ? (
                            <Typography variant="body2" color="text.primary">
                              {station.externalId}
                            </Typography>
                          ) : (
                            <Typography variant="body2" color="text.secondary">
                              Não definido
                            </Typography>
                          )}
                        </TableCell>
                        <TableCell align="right">
                          {station.locationId ? (
                            <Button
                              size="small"
                              variant="outlined"
                              color="error"
                              onClick={() => handleOpenUnassignDialog(station)}
                              disabled={assignLoading}
                            >
                              Desatribuir Localização
                            </Button>
                          ) : (
                            <Button
                              size="small"
                              variant="outlined"
                              onClick={() => handleOpenAssignDialog(station)}
                              disabled={assignLoading}
                            >
                              Atribuir Localização
                            </Button>
                          )}
                        </TableCell>
                      </TableRow>
                    ))
                  )}
                </TableBody>
              </Table>
            </TableContainer>
            <TablePagination
              rowsPerPageOptions={[10, 25, 50]}
              component="div"
              count={totalStations}
              rowsPerPage={stationRowsPerPage}
              page={stationPage}
              onPageChange={handleStationPageChange}
              onRowsPerPageChange={handleStationRowsPerPageChange}
            />
          </Box>

          <Box sx={{ mt: 4 }}>
            <Typography variant="h6" gutterBottom>
              Produtos Disponíveis
            </Typography>
            <Box
              sx={{
                display: 'flex',
                alignItems: 'center',
                mb: 2,
                gap: 2,
              }}
            >
              <TextField
                size="small"
                placeholder="Procurar produtos..."
                value={productSearchTerm}
                onChange={(e) => setProductSearchTerm(e.target.value)}
                onKeyPress={(e) => e.key === 'Enter' && handleSearch()}
                sx={{
                  minWidth: 250,
                  '& .MuiInputBase-root': {
                    height: 40,
                  },
                }}
              />
              <Button variant="contained" startIcon={<Search />} onClick={handleSearch} sx={{ height: 40 }}>
                Procurar
              </Button>
              <Box sx={{ flexGrow: 1 }} />
              <Button variant="contained" startIcon={<Add />} onClick={handleOpenProductDialog} sx={{ height: 40 }}>
                Adicionar Produto
              </Button>
              <Button variant="contained" startIcon={<FileUpload />} onClick={() => setOpenCsvDialog(true)} sx={{ height: 40 }}>
                Importar CSV
              </Button>
              <Button
                variant="contained"
                startIcon={<Sync />}
                onClick={handleSyncProducts}
                disabled={products.length === 0}
                sx={{ height: 40 }}
              >
                Sincronizar Produtos
              </Button>
            </Box>

            <TableContainer component={Paper}>
              <Table>
                <TableHead sx={{ backgroundColor: theme.palette.grey[800] }}>
                  <TableRow>
                    <TableCell>SKU</TableCell>
                    <TableCell>Código Opcional</TableCell>
                    <TableCell>Nome</TableCell>
                    <TableCell>Unidade de Medida</TableCell>
                    <TableCell align="right">Ações</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {productsLoading && products.length === 0 ? (
                    <TableRow>
                      <TableCell colSpan={5} align="center">
                        <CircularProgress />
                      </TableCell>
                    </TableRow>
                  ) : (
                    products.map((product) => (
                      <TableRow key={product.id} hover>
                        <TableCell>{product.sku}</TableCell>
                        <TableCell>{product.optionalCode}</TableCell>
                        <TableCell>{product.name}</TableCell>
                        <TableCell>{product.measureUnit}</TableCell>
                        <TableCell align="right">
                          <Button size="small" variant="outlined" color="error" onClick={() => handleUnmarkProduct(product.id)}>
                            Remover
                          </Button>
                        </TableCell>
                      </TableRow>
                    ))
                  )}
                </TableBody>
              </Table>
              <TablePagination
                rowsPerPageOptions={[50]}
                component="div"
                count={-1}
                rowsPerPage={rowsPerPage}
                page={page}
                onPageChange={handleChangePage}
                onRowsPerPageChange={handleChangeRowsPerPage}
                labelDisplayedRows={({ from, to }) => `${from}-${to}`}
                showFirstButton={false}
                showLastButton={false}
                slotProps={{
                  actions: {
                    nextButton: {
                      disabled: !nextOffset,
                    },
                    previousButton: {
                      disabled: page === 0,
                    },
                  },
                }}
              />
            </TableContainer>
          </Box>

          <RegisterStationDialog open={openStationDialog} onClose={() => setOpenStationDialog(false)} onSubmit={handleRegisterStation} />
          <AsrsProductSelectionDialog
            open={openProductDialog}
            onClose={() => setOpenProductDialog(false)}
            onProductSelect={(productId: string) => handleMarkProduct(productId)}
          />
          <AssignLocationToStationDialog
            open={assignDialogOpen}
            onClose={() => setAssignDialogOpen(false)}
            onAssign={handleAssignLocation}
            warehouseId={selectedStation?.warehouseId || ''}
            loading={assignLoading}
          />
          <ConfirmActionDialog
            open={unassignDialogOpen}
            title="Confirmar Desatribuição"
            message="Tem certeza que deseja desatribuir esta localização da estação?"
            loading={assignLoading}
            onClose={() => setUnassignDialogOpen(false)}
            onConfirm={handleConfirmUnassign}
          />
          {serverId ? (
            <AsrsServerProductCsvDialog open={openCsvDialog} onClose={() => setOpenCsvDialog(false)} serverId={serverId} />
          ) : (
            <></>
          )}
        </>
      ) : (
        <Typography variant="body2" color="error">
          Servidor não encontrado
        </Typography>
      )}
    </Box>
  );
};

export default AsrsServerPage;
